import {getMonthEnd, getMonthStart} from '@fluentui/date-time-utilities';
import {getTheme} from '@fluentui/react';
import React, {CSSProperties, useMemo} from 'react';
import {Cell, CellProps} from 'react-table';
import styled from 'styled-components';
import {buildBackgroundImage} from '../common/calendar/backgroundImage';
import {DateRangeSelector} from '../common/calendar/DateRangeSelector';
import {EventBars, onClickEventFn} from '../common/calendar/EventBar';
import {MonthSelector} from '../common/calendar/MonthSelector';
import {DateRange} from '../common/DateRange';
import {
  CheckboxProps,
  ResourceDetailsTable,
  StickyColumn,
} from '../common/ResourceDetailsTable';
import {nullColumn} from '../common/table/Table';
import {COMMAND_BAR_HEIGHT} from '../consts';
import {Actions} from '../types/Action';
import {ResourceDetails} from '../types/ResourceDetails';
import {ResourceList} from '../types/ResourceList';
import {SortOrder} from '../types/SortOrder';
import {TableTool} from '../types/Table';
import {TableColumn} from '../types/TableColumn';
import {ToolButton} from '../types/ToolButton';
import {formatDateWithWeekday} from '../widgets/common/dateutil';
import {buildColumns} from './GridList';
import {Sorter} from '../types/Sorter';
import {buildCalendarMod, CalendarMod} from '../common/calendar/CalendarMod';
import {getDateText} from '../dates/dates';
import {textColor} from '../styles';

export type RowCalendarListProps = {
  type: string;
  schemaId: string;
  buttons?: ToolButton[];
  farButtons?: ToolButton[];
  sorters?: Sorter[];
  tableTools?: TableTool[];
  tableFarTools?: TableTool[];
  columns?: TableColumn[];
  allowAdjacent?: boolean;
  customDateRange?: boolean;
  customDateRangeMaxLength?: number;
  checkbox?: boolean;
  unitWidth?: number;
};

type Props = {
  resourceList: ResourceList;
  items: ResourceDetails[];
  columns?: TableColumn[];
  events: {[itemId: string]: ResourceDetails};
  dateRange: DateRange;
  onChangeDateRange: (start: Date, end: Date) => void;
  onSelect: (item: ResourceDetails) => void;
  onClickEvent?: onClickEventFn;
  tableTools?: TableTool[];
  tableFarTools?: TableTool[];
  onChangePageSize: (size: number) => void;
  onChangeCurrentPage: (index: number) => void;
  onChangeSorter: (id: string, current: SortOrder) => void;
  allowAdjacent?: boolean;
  customDateRange?: boolean;
  customDateRangeMaxLength?: number;
  actions: Actions;
  unitWidth?: number;
} & CheckboxProps;

export function RowCalendarList(props: Props): JSX.Element {
  const mod = useMemo(() => {
    return buildCalendarMod(props.resourceList);
  }, [props.resourceList]);

  const eventColumns = buildEventColumns(props, mod);

  const cols = useMemo(() => {
    return buildFieldColumns(buildColumns(props.resourceList, props.columns));
  }, [props.resourceList, props.columns]);

  return (
    <ResourceDetailsTable
      expandLastColumn={true}
      {...props}
      columns={cols}
      columnsDecorator={(columns) => {
        return [
          ...columns.map((c) => {
            c.sticky = 'left';
            return c;
          }),
          ...eventColumns,
        ];
      }}
      headerDecorator={(header: JSX.Element) => {
        return (
          <HeaderContainer>
            {buildMonthSelector(props)}
            {header}
          </HeaderContainer>
        );
      }}
    />
  );
}

const defaultUnitWidth = 36;

function buildEventColumns(props: Props, mod?: CalendarMod): StickyColumn[] {
  const unitWidth = props.unitWidth || defaultUnitWidth;

  const image = buildBackgroundImage(props.dateRange, unitWidth, mod);
  const style = {backgroundImage: `url(${image})`};

  return [
    {
      Header: () => {
        return buildDaysHeader(props.dateRange, style, unitWidth, mod);
      },
      Cell: (cellProps: CellProps<ResourceDetails>) => {
        const contents = buildContents(
          props.events,
          props.dateRange,
          cellProps.cell,
          unitWidth,
          props.onClickEvent,
          props.allowAdjacent,
        );
        return <Days style={style}>{contents}</Days>;
      },
      width: unitWidth * props.dateRange.len,
      accessor: '__events__',
    },
    nullColumn,
  ];
}

function buildFieldColumns(columns?: TableColumn[]): TableColumn[] {
  if (!columns || columns.length === 0) {
    return defaultColumns;
  }

  return columns;
}

const defaultColumns: TableColumn[] = [
  {
    fieldId: 'name',
    label: '名称',
    visible: true,
    width: 200,
    styles: {
      body: {
        whiteSpace: 'normal',
      },
    },
  },
];

function buildMonthSelector(props: Props): JSX.Element {
  return (
    <MonthSelectorContainer>
      {props.customDateRange ? (
        <DateRangeSelector
          dateRange={props.dateRange}
          onChangeDateRange={(range) => {
            props.onChangeDateRange(range.start, range.end);
          }}
          maxLength={props.customDateRangeMaxLength}
          style={{marginLeft: '0.5rem'}}
        />
      ) : (
        <MonthSelector
          month={new Date()}
          onSelect={(month: Date) => {
            const start = getMonthStart(month);
            const end = getMonthEnd(month);
            props.onChangeDateRange(start, end);
          }}
        />
      )}
    </MonthSelectorContainer>
  );
}

function buildDaysHeader(
  dateRange: DateRange,
  style: CSSProperties,
  unitWidth: number,
  mod?: CalendarMod,
): JSX.Element {
  const days: JSX.Element[] = [];

  dateRange.eachDate((date, i) => {
    days.push(
      <Day key={`header-${i}`} date={date} unitWidth={unitWidth} mod={mod} />,
    );
  });

  return <Days style={style}>{days}</Days>;
}

type DayProps = {
  date: Date;
  unitWidth: number;
  mod?: CalendarMod;
};

function Day({date, unitWidth, mod}: DayProps): JSX.Element {
  const color = selectColor(date, mod);
  const title = buildTitle(date, mod);
  const d = date.getDate();

  return (
    <HeaderDay unitWidth={unitWidth} color={color} title={title}>
      {d}
    </HeaderDay>
  );
}

function buildTitle(date: Date, mod?: CalendarMod) {
  const title = formatDateWithWeekday(date);

  if (!mod) {
    return title;
  }

  const ymd = getDateText(date);
  const sub = mod.dateMap[ymd]?.name;

  if (!sub) {
    return title;
  }

  return title + ' ' + sub;
}

function selectColor(date: Date, mod?: CalendarMod): string {
  const ymd = getDateText(date);

  if (ymd && mod) {
    const color = mod.dateMap[ymd]?.color;

    if (color) {
      return color;
    }
  }

  return selectColorByWeekDay(date.getDay());
}

function selectColorByWeekDay(weekDay: number): string {
  switch (weekDay) {
    case 0:
      return theme.palette.red;
    case 6:
      return theme.palette.blueMid;
    default:
      return textColor;
  }
}

const buildContents = (
  events: {[key: string]: ResourceDetails},
  dateRange: DateRange,
  cell: Cell<ResourceDetails>,
  unitWidth: number,
  onClickEvent?: onClickEventFn,
  allowAdjacent?: boolean,
) => {
  const eventIds: string[] = cell.row.original['events'] || [];
  const evts = eventIds.filter((id) => !!events[id]).map((id) => events[id]);

  return (
    <EventBars
      events={evts}
      onClickEvent={onClickEvent}
      dateRange={dateRange}
      unitWidth={unitWidth}
      allowAdjacent={allowAdjacent}
    />
  );
};

const theme = getTheme();

const HeaderContainer = styled.div`
  display: flex;
`;

const MonthSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: ${COMMAND_BAR_HEIGHT}px;
`;

type CalendarProps = {
  unitWidth: number;
  color: string;
};

function calendarStyle(props: CalendarProps) {
  return {
    style: {
      width: props.unitWidth + 'px',
      color: props.color,
    },
  };
}

const HeaderDay = styled.div.attrs<CalendarProps>(
  calendarStyle,
)<CalendarProps>``;

const Days = styled.div`
  display: flex;
  align-items: center;
  background-repeat: repeat;
  height: 100%;
  width: 100%;
`;
